package com.nonopn.skywatcher.design.widgets

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.Icon
import androidx.compose.material.DropdownMenu
import androidx.compose.material.DropdownMenuItem
import androidx.compose.material.IconButton
import androidx.compose.material.icons.Icons
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.rememberVectorPainter
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties

import com.nonopn.icons.SkipNext
import com.nonopn.icons.SkipPrevious
import com.nonopn.skywatcher.design.res.Res
import com.nonopn.skywatcher.design.res.april
import com.nonopn.skywatcher.design.res.august
import com.nonopn.skywatcher.design.res.december
import com.nonopn.skywatcher.design.res.february
import com.nonopn.skywatcher.design.res.january
import com.nonopn.skywatcher.design.res.july
import com.nonopn.skywatcher.design.res.june
import com.nonopn.skywatcher.design.res.march
import com.nonopn.skywatcher.design.res.may
import com.nonopn.skywatcher.design.res.november
import com.nonopn.skywatcher.design.res.october
import com.nonopn.skywatcher.design.res.end_date_label
import com.nonopn.skywatcher.design.res.period_label
import com.nonopn.skywatcher.design.res.start_date_label
import com.nonopn.skywatcher.design.res.september
import com.nonopn.skywatcher.design.res.single_date_label
import com.nonopn.skywatcher.design.res.today_label
import com.nonopn.skywatcher.design.res.validate
import com.nonopn.skywatcher.design.theme.AppColor
import com.nonopn.skywatcher.design.utils.localized

import epicarchitect.calendar.compose.basis.EpicMonth
import epicarchitect.calendar.compose.basis.config.rememberBasisEpicCalendarConfig
import epicarchitect.calendar.compose.datepicker.EpicDatePicker
import epicarchitect.calendar.compose.datepicker.config.rememberEpicDatePickerConfig
import epicarchitect.calendar.compose.datepicker.state.EpicDatePickerState
import epicarchitect.calendar.compose.datepicker.state.rememberEpicDatePickerState
import epicarchitect.calendar.compose.pager.config.rememberEpicCalendarPagerConfig
import eu.codlab.compose.widgets.TextNormal
import korlibs.io.async.launch
import korlibs.time.DateTime
import korlibs.time.DateTimeTz
import korlibs.time.days
import korlibs.time.hours
import korlibs.time.milliseconds
import korlibs.time.minutes
import korlibs.time.months
import korlibs.time.seconds
import org.jetbrains.compose.resources.StringResource

import kotlinx.datetime.Month

private object Colors {
    val LightGray = Color(0xFFF0F0F0)
    val VeryLightGray = Color(0xFFF5F5F5)
}

@Composable
fun DatePickerDialog(
    defaultDate: DateTimeTz? = null,
    onDismiss: () -> Unit,
    onRange: (DateTimeTz, DateTimeTz) -> Unit,
) {
    DatePickerDialogInternal(
        defaultDate = defaultDate,
        onDismiss = onDismiss,
        onRange = onRange
    )
}

@Composable
fun DatePickerDialog(
    defaultDate: DateTimeTz? = null,
    onDismiss: () -> Unit,
    onDate: (DateTimeTz) -> Unit
) {
    DatePickerDialogInternal(
        defaultDate = defaultDate,
        onDismiss = onDismiss,
        onDate = onDate
    )
}

@Composable
fun DatePickerDialog(
    defaultDate: DateTimeTz? = null,
    defaultRangeStart: DateTimeTz? = null,
    initialRangeMode: Boolean = false,
    onDismiss: () -> Unit,
    onDate: (DateTimeTz) -> Unit,
    onRange: (DateTimeTz, DateTimeTz) -> Unit,
) {
    DatePickerDialogInternal(
        defaultDate = defaultDate,
        defaultRangeStart = defaultRangeStart,
        initialRangeMode = initialRangeMode,
        onDismiss = onDismiss,
        onDate = onDate,
        onRange = onRange,
        showModeTabs = true
    )
}

@Suppress("LongMethod")
@Composable
private fun DatePickerDialogInternal(
    defaultDate: DateTimeTz? = null,
    defaultRangeStart: DateTimeTz? = null,
    initialRangeMode: Boolean = false,
    onDismiss: () -> Unit,
    onRange: ((DateTimeTz, DateTimeTz) -> Unit)? = null,
    onDate: ((DateTimeTz) -> Unit)? = null,
    showModeTabs: Boolean = false
) {
    val initialMonth =
        defaultDate?.let { EpicMonth(it.yearInt, it.month.toDateTimeMonth) } ?: EpicMonth.now()
    val coroutine = rememberCoroutineScope()

    var isRangeMode by remember {
        mutableStateOf(
            if (showModeTabs) initialRangeMode
            else (onRange != null && onDate == null)
        )
    }
    var showMonthPicker by remember { mutableStateOf(false) }
    var monthPickerYear by remember { mutableStateOf(initialMonth.year) }

    val selectionMode = if (isRangeMode) {
        EpicDatePickerState.SelectionMode.Range
    } else {
        EpicDatePickerState.SelectionMode.Single()
    }

    val state = rememberEpicDatePickerState(
        initialMonth = initialMonth,
        config = rememberEpicDatePickerConfig(
            pagerConfig = rememberEpicCalendarPagerConfig(
                basisConfig = rememberBasisEpicCalendarConfig(
                    displayDaysOfAdjacentMonths = false
                )
            ),
            selectionContentColor = MaterialTheme.colorScheme.onPrimary,
            selectionContainerColor = MaterialTheme.colorScheme.primary
        ),
        selectionMode = selectionMode,
    )

    LaunchedEffect(isRangeMode) {
        state.selectionMode = if (isRangeMode) {
            EpicDatePickerState.SelectionMode.Range
        } else {
            EpicDatePickerState.SelectionMode.Single()
        }
    }

    LaunchedEffect(state.selectedDates) {
        if (state.selectedDates.isNotEmpty()) {
            val dates = state.selectedDates.map {
                DateTime.invoke(
                    year = it.year,
                    month = it.monthNumber,
                    day = it.dayOfMonth,
                    hour = 12
                )
            }

            if (isRangeMode) {
                if (dates.size >= 2 && onRange != null) {
                    onRange(dates.first().local.startOfDay, dates.last().local.endOfDay)
                }
            } else {
                onDate?.invoke(dates.first().local.endOfDay)
            }
        }
    }

    val currentMonth = state.pagerState.currentMonth
    val monthLabel = currentMonth.month.translation.localized()
    val year = currentMonth.year

    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties()
    ) {
        DefaultCard(
            backgroundColor = AppColor.White
        ) {
            Column(
                modifier = Modifier.padding(12.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                if (showModeTabs) {
                    DateModeSelector(isRangeMode) { isRangeMode = it }
                    Spacer(Modifier.height(8.dp))
                }

                if (isRangeMode && showModeTabs) {
                    DateRangeDropdowns(
                        defaultStartDate = defaultRangeStart,
                        defaultEndDate = defaultDate,
                        onValidate = { from, to ->
                            onRange?.invoke(from, to)
                        }
                    )
                } else if (showMonthPicker) {
                    MonthYearPicker(
                        year = monthPickerYear,
                        currentMonth = currentMonth,
                        onYearChange = { monthPickerYear = it },
                        onMonthSelected = { selected ->
                            val delta = (monthPickerYear - currentMonth.year) * 12 +
                                (selected.ordinal - currentMonth.month.ordinal)
                            coroutine.launch { state.pagerState.scrollMonths(delta) }
                            showMonthPicker = false
                        }
                    )
                } else {
                    Row(
                        Modifier.fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        IconButton(
                            modifier = Modifier.width(24.dp).height(24.dp),
                            onClick = {
                                coroutine.launch {
                                    state.pagerState.scrollMonths(-1)
                                }
                            }
                        ) {
                            Icon(
                                modifier = Modifier.width(24.dp).height(24.dp),
                                painter = rememberVectorPainter(Icons.Default.SkipPrevious),
                                tint = AppColor.Black,
                                contentDescription = "previous"
                            )
                        }
                        Column(Modifier.weight(1.0f)) { }
                        TextNormal(
                            modifier = Modifier
                                .clip(RoundedCornerShape(8.dp))
                                .clickable {
                                    monthPickerYear = year
                                    showMonthPicker = true
                                }
                                .padding(horizontal = 12.dp, vertical = 4.dp),
                            color = Color.Black,
                            text = "$monthLabel $year",
                            fontWeight = FontWeight.SemiBold
                        )
                        Column(Modifier.weight(1.0f)) { }
                        IconButton(
                            modifier = Modifier.width(24.dp).height(24.dp),
                            onClick = {
                                coroutine.launch {
                                    state.pagerState.scrollMonths(1)
                                }
                            }
                        ) {
                            Icon(
                                painter = rememberVectorPainter(Icons.Default.SkipNext),
                                tint = AppColor.Black,
                                contentDescription = "next"
                            )
                        }
                    }

                    EpicDatePicker(state = state)

                    Spacer(Modifier.height(8.dp))

                    TextNormal(
                        modifier = Modifier
                            .clip(RoundedCornerShape(8.dp))
                            .clickable {
                                val now = EpicMonth.now()
                                val delta = (now.year - currentMonth.year) * 12 +
                                    (now.month.ordinal - currentMonth.month.ordinal)
                                coroutine.launch { state.pagerState.scrollMonths(delta) }
                                if (!isRangeMode) {
                                    onDate?.invoke(DateTime.now().local.endOfDay)
                                }
                            }
                            .background(Colors.LightGray, RoundedCornerShape(8.dp))
                            .padding(horizontal = 16.dp, vertical = 8.dp),
                        color = AppColor.Black,
                        text = Res.string.today_label.localized()
                    )
                }
            }
        }
    }
}

@Composable
private fun DateModeSelector(
    isRangeMode: Boolean,
    onModeChange: (Boolean) -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(8.dp))
            .background(Colors.LightGray)
            .padding(4.dp),
        horizontalArrangement = Arrangement.Center
    ) {
        DateModeChip(
            modifier = Modifier.weight(1f),
            text = Res.string.single_date_label.localized(),
            selected = !isRangeMode,
            onClick = { onModeChange(false) }
        )
        DateModeChip(
            modifier = Modifier.weight(1f),
            text = Res.string.period_label.localized(),
            selected = isRangeMode,
            onClick = { onModeChange(true) }
        )
    }
}

@Composable
private fun DateModeChip(
    modifier: Modifier = Modifier,
    text: String,
    selected: Boolean,
    onClick: () -> Unit
) {
    Box(
        modifier = modifier
            .clip(RoundedCornerShape(6.dp))
            .then(
                if (selected) Modifier.background(AppColor.White, RoundedCornerShape(6.dp))
                else Modifier
            )
            .clickable(onClick = onClick)
            .padding(vertical = 8.dp),
        contentAlignment = Alignment.Center
    ) {
        TextNormal(
            text = text,
            color = if (selected) AppColor.Black else Color.Gray,
            fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Normal
        )
    }
}

@Composable
private fun MonthYearPicker(
    year: Int,
    currentMonth: EpicMonth,
    onYearChange: (Int) -> Unit,
    onMonthSelected: (Month) -> Unit
) {
    val allMonths = listOf(
        Month.JANUARY, Month.FEBRUARY, Month.MARCH, Month.APRIL,
        Month.MAY, Month.JUNE, Month.JULY, Month.AUGUST,
        Month.SEPTEMBER, Month.OCTOBER, Month.NOVEMBER, Month.DECEMBER
    )

    Column(horizontalAlignment = Alignment.CenterHorizontally) {
        Row(
            Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            IconButton(
                modifier = Modifier.width(24.dp).height(24.dp),
                onClick = { onYearChange(year - 1) }
            ) {
                Icon(
                    modifier = Modifier.width(24.dp).height(24.dp),
                    painter = rememberVectorPainter(Icons.Default.SkipPrevious),
                    tint = AppColor.Black,
                    contentDescription = "previous year"
                )
            }
            TextNormal("$year", color = AppColor.Black, fontWeight = FontWeight.Bold, fontSize = 18.sp)
            IconButton(
                modifier = Modifier.width(24.dp).height(24.dp),
                onClick = { onYearChange(year + 1) }
            ) {
                Icon(
                    modifier = Modifier.width(24.dp).height(24.dp),
                    painter = rememberVectorPainter(Icons.Default.SkipNext),
                    tint = AppColor.Black,
                    contentDescription = "next year"
                )
            }
        }

        Spacer(Modifier.height(8.dp))

        for (row in 0 until 4) {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                for (col in 0 until 3) {
                    val month = allMonths[row * 3 + col]
                    val isCurrent = month == currentMonth.month && year == currentMonth.year

                    Box(
                        modifier = Modifier
                            .weight(1f)
                            .clip(RoundedCornerShape(8.dp))
                            .background(
                                if (isCurrent) MaterialTheme.colorScheme.primary
                                else Colors.VeryLightGray,
                                RoundedCornerShape(8.dp)
                            )
                            .clickable { onMonthSelected(month) }
                            .padding(vertical = 12.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        TextNormal(
                            month.translation.localized(),
                            color = if (isCurrent) MaterialTheme.colorScheme.onPrimary
                            else AppColor.Black
                        )
                    }
                }
            }
            if (row < 3) Spacer(Modifier.height(8.dp))
        }
    }
}

private val allMonthsList = listOf(
    Month.JANUARY, Month.FEBRUARY, Month.MARCH, Month.APRIL,
    Month.MAY, Month.JUNE, Month.JULY, Month.AUGUST,
    Month.SEPTEMBER, Month.OCTOBER, Month.NOVEMBER, Month.DECEMBER
)

private fun daysInMonth(month: Month, year: Int): Int = when (month) {
    Month.FEBRUARY -> if (year % 4 == 0 && (year % 100 != 0 || year % 400 == 0)) 29 else 28
    Month.APRIL, Month.JUNE, Month.SEPTEMBER, Month.NOVEMBER -> 30
    else -> 31
}

@Suppress("LongMethod")
@Composable
private fun DateRangeDropdowns(
    defaultStartDate: DateTimeTz? = null,
    defaultEndDate: DateTimeTz? = null,
    onValidate: (DateTimeTz, DateTimeTz) -> Unit
) {
    val now = DateTime.now().local
    val minYear = 2025
    val minMonthIndex = Month.APRIL.ordinal
    val currentYear = now.yearInt

    var startDay by remember { mutableStateOf(defaultStartDate?.dayOfMonth ?: 1) }
    var startMonth by remember {
        mutableStateOf(defaultStartDate?.month?.toDateTimeMonth ?: Month.APRIL)
    }
    var startYear by remember { mutableStateOf(defaultStartDate?.yearInt ?: minYear) }

    var endDay by remember { mutableStateOf(defaultEndDate?.dayOfMonth ?: now.dayOfMonth) }
    var endMonth by remember {
        mutableStateOf(defaultEndDate?.month?.toDateTimeMonth ?: now.month.toDateTimeMonth)
    }
    var endYear by remember { mutableStateOf(defaultEndDate?.yearInt ?: currentYear) }

    val startMaxDays = daysInMonth(startMonth, startYear)
    if (startDay > startMaxDays) startDay = startMaxDays

    val endMaxDays = daysInMonth(endMonth, endYear)
    if (endDay > endMaxDays) endDay = endMaxDays

    val years = (minYear..currentYear).toList()
    val startMonths = if (startYear == minYear) {
        allMonthsList.filter { it.ordinal >= minMonthIndex }
    } else {
        allMonthsList
    }
    if (startMonth !in startMonths) startMonth = startMonths.first()

    Column(horizontalAlignment = Alignment.CenterHorizontally) {
        TextNormal(
            text = Res.string.start_date_label.localized(),
            fontWeight = FontWeight.SemiBold,
            color = Color.Black
        )
        Spacer(Modifier.height(4.dp))
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(4.dp)) {
            NumberDropdown(Modifier.weight(1f), startDay, (1..startMaxDays).toList()) {
                startDay = it
            }
            MonthDropdown(Modifier.weight(1.5f), startMonth, startMonths) { startMonth = it }
            NumberDropdown(Modifier.weight(1f), startYear, years) { startYear = it }
        }

        Spacer(Modifier.height(12.dp))

        TextNormal(
            text = Res.string.end_date_label.localized(),
            fontWeight = FontWeight.SemiBold,
            color = Color.Black
        )
        Spacer(Modifier.height(4.dp))
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(4.dp)) {
            NumberDropdown(Modifier.weight(1f), endDay, (1..endMaxDays).toList()) { endDay = it }
            MonthDropdown(Modifier.weight(1.5f), endMonth, allMonthsList) { endMonth = it }
            NumberDropdown(Modifier.weight(1f), endYear, years) { endYear = it }
        }

        Spacer(Modifier.height(16.dp))

        TextNormal(
            modifier = Modifier
                .clip(RoundedCornerShape(8.dp))
                .clickable {
                    val from = DateTime.invoke(
                        year = startYear,
                        month = startMonth.ordinal + 1,
                        day = startDay,
                        hour = 12
                    ).local.startOfDay
                    val to = DateTime.invoke(
                        year = endYear,
                        month = endMonth.ordinal + 1,
                        day = endDay,
                        hour = 12
                    ).local.endOfDay
                    onValidate(from, to)
                }
                .background(Colors.LightGray, RoundedCornerShape(8.dp))
                .padding(horizontal = 16.dp, vertical = 8.dp),
            color = AppColor.Black,
            text = Res.string.validate.localized()
        )
    }
}

@Composable
private fun NumberDropdown(
    modifier: Modifier,
    selected: Int,
    options: List<Int>,
    onSelect: (Int) -> Unit
) {
    var expanded by remember { mutableStateOf(false) }
    Box(modifier) {
        TextNormal(
            modifier = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(8.dp))
                .background(Colors.VeryLightGray, RoundedCornerShape(8.dp))
                .clickable { expanded = true }
                .padding(horizontal = 8.dp, vertical = 10.dp),
            text = selected.toString(),
            color = Color.Black,
            textAlign = TextAlign.Center
        )
        DropdownMenu(expanded = expanded, onDismissRequest = { expanded = false }) {
            options.forEach { option ->
                DropdownMenuItem(onClick = { onSelect(option); expanded = false }) {
                    TextNormal(text = option.toString(), color = Color.Black)
                }
            }
        }
    }
}

@Composable
private fun MonthDropdown(
    modifier: Modifier,
    selected: Month,
    options: List<Month>,
    onSelect: (Month) -> Unit
) {
    var expanded by remember { mutableStateOf(false) }
    Box(modifier) {
        TextNormal(
            modifier = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(8.dp))
                .background(Colors.VeryLightGray, RoundedCornerShape(8.dp))
                .clickable { expanded = true }
                .padding(horizontal = 8.dp, vertical = 10.dp),
            text = selected.translation.localized(),
            color = Color.Black,
            textAlign = TextAlign.Center
        )
        DropdownMenu(expanded = expanded, onDismissRequest = { expanded = false }) {
            options.forEach { option ->
                DropdownMenuItem(onClick = { onSelect(option); expanded = false }) {
                    TextNormal(text = option.translation.localized(), color = Color.Black)
                }
            }
        }
    }
}

val Month.translation: StringResource
    get() = when (this) {
        Month.JANUARY -> Res.string.january
        Month.FEBRUARY -> Res.string.february
        Month.MARCH -> Res.string.march
        Month.APRIL -> Res.string.april
        Month.MAY -> Res.string.may
        Month.JUNE -> Res.string.june
        Month.JULY -> Res.string.july
        Month.AUGUST -> Res.string.august
        Month.SEPTEMBER -> Res.string.september
        Month.OCTOBER -> Res.string.october
        Month.NOVEMBER -> Res.string.november
        Month.DECEMBER -> Res.string.december
        else -> Res.string.january
    }

val korlibs.time.Month.toDateTimeMonth: Month
    get() = when (this) {
        korlibs.time.Month.January -> Month.JANUARY
        korlibs.time.Month.February -> Month.FEBRUARY
        korlibs.time.Month.March -> Month.MARCH
        korlibs.time.Month.April -> Month.APRIL
        korlibs.time.Month.May -> Month.MAY
        korlibs.time.Month.June -> Month.JUNE
        korlibs.time.Month.July -> Month.JULY
        korlibs.time.Month.August -> Month.AUGUST
        korlibs.time.Month.September -> Month.SEPTEMBER
        korlibs.time.Month.October -> Month.OCTOBER
        korlibs.time.Month.November -> Month.NOVEMBER
        korlibs.time.Month.December -> Month.DECEMBER
    }

val DateTimeTz.startOfDay: DateTimeTz
    get() = this.minus(this.hours.hours)
        .minus(this.minutes.minutes)
        .minus(this.seconds.seconds)
        .minus(this.milliseconds.milliseconds)

val DateTimeTz.endOfDay: DateTimeTz
    get() = this.add(0.months, 1.days).startOfDay.minus(1.milliseconds)
